import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PenLine, Send, BadgeCheck, UserRound, ChevronDown, FileText, Trash2, Compass, ImagePlus, Mail } from "lucide-react";
import api, { timeAgo, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_ROUTES } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { AuthorEarnings } from "@/components/AuthorEarnings";
import { AuthorOnboarding } from "@/components/studio/AuthorOnboarding";
import { StudioTour } from "@/components/studio/StudioTour";
import { AvatarCropDialog, getCroppedBlob } from "@/components/studio/AvatarCropDialog";
import { StoryInsights } from "@/components/studio/StoryInsights";
import type { Area } from "react-easy-crop";

const STATUS_STYLE: Record<string, string> = {
  published: "bg-green-500/15 text-green-400 border-green-500/30",
  imported_pending_review: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  needs_changes: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  rejected: "bg-red-500/15 text-red-400 border-red-500/30",
};

const EMPTY = { title: "", summary: "", body: "", hero_image_url: "", category: "crypto" };

const ProfileCard = ({ app }: { app: any }) => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const wantProfile = searchParams.get("profile") === "1";
  const cardRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(wantProfile);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const pickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result as string);
    reader.readAsDataURL(file);
  };
  const saveCropped = async (area: Area) => {
    if (!cropSrc) return;
    setUploading(true);
    try {
      const blob = await getCroppedBlob(cropSrc, area);
      const fd = new FormData();
      fd.append("file", blob, "avatar.jpg");
      const { data } = await api.post("/author/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setP((prev) => ({ ...prev, avatar_url: data.avatar_url }));
      toast.success("Profile photo updated");
      queryClient.invalidateQueries({ queryKey: ["author-program-me"] });
      setCropSrc(null);
    } catch (err) { toast.error(apiError(err)); }
    setUploading(false);
  };
  useEffect(() => {
    if (wantProfile) {
      const t = setTimeout(() => cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 350);
      return () => clearTimeout(t);
    }
  }, [wantProfile]);
  const [p, setP] = useState({
    display_name: app.display_name || "", handle: app.handle || "", bio: app.bio || "",
    avatar_url: app.avatar_url || "", x: app.socials?.x || "", website: app.socials?.website || "",
  });
  const [emailPrefs, setEmailPrefs] = useState({
    weekly_digest: true, milestones: true, ...(app.email_prefs || {}),
  });
  const togglePref = async (key: "weekly_digest" | "milestones", value: boolean) => {
    setEmailPrefs((prev) => ({ ...prev, [key]: value }));
    try {
      await api.patch("/author/email-prefs", { [key]: value });
      toast.success(value ? "Emails switched on" : "Emails switched off");
    } catch (err) {
      toast.error(apiError(err));
      setEmailPrefs((prev) => ({ ...prev, [key]: !value }));
    }
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/author/profile", {
        display_name: p.display_name, handle: p.handle, bio: p.bio,
        avatar_url: p.avatar_url, socials: { x: p.x, website: p.website },
      });
      toast.success("Public profile updated");
      queryClient.invalidateQueries({ queryKey: ["author-program-me"] });
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const field = "bg-[#1A1A1A] border-white/10 text-white";
  return (
    <div ref={cardRef} className="mt-6 scroll-mt-24 bg-[#121212] border border-white/10 rounded-xl overflow-hidden" data-testid="studio-profile-card">
      <button type="button" onClick={() => setOpen(!open)} data-testid="studio-profile-toggle"
        className="w-full flex items-center justify-between px-6 py-4 text-left">
        <span className="font-heading font-bold text-white text-sm flex items-center gap-2">
          <UserRound className="h-4 w-4 text-[#A78BFA]" /> Your public profile
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-6 pb-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Display name</Label>
            <Input value={p.display_name} onChange={(e) => setP({ ...p, display_name: e.target.value })}
              data-testid="studio-profile-name" className={`mt-1 ${field}`} />
          </div>
          <div>
            <Label>Handle</Label>
            <Input value={p.handle} onChange={(e) => setP({ ...p, handle: e.target.value })}
              placeholder="a–z, 0–9, underscore" data-testid="studio-profile-handle" className={`mt-1 font-mono ${field}`} />
          </div>
          <div className="sm:col-span-2">
            <Label>Bio</Label>
            <Textarea value={p.bio} rows={2} onChange={(e) => setP({ ...p, bio: e.target.value })}
              placeholder="Who you are and what you cover — shown on your public byline page"
              data-testid="studio-profile-bio" className={`mt-1 ${field}`} />
          </div>
          <div className="sm:col-span-2">
            <Label>Profile photo</Label>
            <div className="mt-1.5 flex items-center gap-3">
              {p.avatar_url ? (
                <img src={p.avatar_url} alt="Avatar preview" data-testid="studio-avatar-preview"
                  className="h-12 w-12 rounded-full object-cover border border-white/15 shrink-0" />
              ) : (
                <div data-testid="studio-avatar-placeholder"
                  className="h-12 w-12 rounded-full bg-[#7C3AED]/20 border border-[#7C3AED]/40 flex items-center justify-center shrink-0">
                  <UserRound className="h-5 w-5 text-[#A78BFA]" />
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden"
                data-testid="studio-avatar-file" onChange={pickFile} />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}
                disabled={uploading} data-testid="studio-avatar-upload-button"
                className="border-white/20 bg-transparent text-white hover:bg-white/5">
                <ImagePlus className="h-4 w-4 mr-1.5" />
                {uploading ? "Uploading…" : p.avatar_url ? "Change photo" : "Upload photo"}
              </Button>
              <span className="text-[11px] text-gray-500">JPG, PNG or WebP · up to 3MB · shown on your byline</span>
            </div>
          </div>
          <div>
            <Label>X / Twitter URL</Label>
            <Input value={p.x} onChange={(e) => setP({ ...p, x: e.target.value })}
              placeholder="https://x.com/you" data-testid="studio-profile-x" className={`mt-1 ${field}`} />
          </div>
          <div>
            <Label>Website URL</Label>
            <Input value={p.website} onChange={(e) => setP({ ...p, website: e.target.value })}
              placeholder="https://…" data-testid="studio-profile-website" className={`mt-1 ${field}`} />
          </div>
          <div className="sm:col-span-2 rounded-lg border border-white/[0.08] bg-black/30 px-4 py-4" data-testid="studio-email-prefs">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 flex items-center gap-1.5 mb-3">
              <Mail className="h-3.5 w-3.5 text-[#A78BFA]" /> Email preferences
            </p>
            <div className="flex items-center justify-between gap-4 py-1.5">
              <div>
                <p className="text-white text-sm font-medium">Weekly performance digest</p>
                <p className="text-gray-500 text-xs mt-0.5">Monday summary of your views and best-performing story</p>
              </div>
              <Switch checked={emailPrefs.weekly_digest} data-testid="studio-pref-weekly-digest"
                onCheckedChange={(v: boolean) => togglePref("weekly_digest", v)} />
            </div>
            <div className="flex items-center justify-between gap-4 py-1.5">
              <div>
                <p className="text-white text-sm font-medium">Milestone celebrations</p>
                <p className="text-gray-500 text-xs mt-0.5">A note when a story crosses 100, 1K or 10K views</p>
              </div>
              <Switch checked={emailPrefs.milestones} data-testid="studio-pref-milestones"
                onCheckedChange={(v: boolean) => togglePref("milestones", v)} />
            </div>
          </div>
          <div className="sm:col-span-2">
            <Button onClick={save} disabled={busy} data-testid="studio-profile-save"
              className="bg-[#7C3AED] text-white font-bold hover:bg-[#6a2dd0]">
              {busy ? "Saving…" : "Save profile"}
            </Button>
          </div>
        </div>
      )}
      <AvatarCropDialog src={cropSrc} open={!!cropSrc} busy={uploading}
        onCancel={() => setCropSrc(null)} onSave={saveCropped} />
    </div>
  );
};

export default function AuthorStudio() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [draftId, setDraftId] = useState<string | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [autoSavedAt, setAutoSavedAt] = useState<string | null>(null);
  const [tour, setTour] = useState(false);
  const [wizardDone, setWizardDone] = useState(false);
  const lastSavedRef = useRef("");
  const formRef = useRef(form);
  const draftIdRef = useRef(draftId);
  const editingRef = useRef(editingId);
  formRef.current = form;
  draftIdRef.current = draftId;
  editingRef.current = editingId;

  const { data } = useQuery({
    queryKey: ["author-program-me"],
    queryFn: async () => (await api.get("/author-program/me")).data,
    enabled: !!user,
  });

  const { data: draftsData } = useQuery({
    queryKey: ["author-drafts"],
    enabled: !!user && data?.author_status === "approved",
    queryFn: async () => (await api.get("/author/drafts")).data,
  });

  useEffect(() => {
    if (!user || data?.author_status !== "approved") return;
    const t = setInterval(async () => {
      const f = formRef.current;
      if (editingRef.current || !f.title.trim()) return;
      const snap = JSON.stringify(f);
      if (snap === lastSavedRef.current) return;
      try {
        if (draftIdRef.current) {
          await api.put(`/author/drafts/${draftIdRef.current}`, f);
        } else {
          const { data: d } = await api.post("/author/drafts", f);
          setDraftId(d.draft.id);
        }
        lastSavedRef.current = snap;
        setAutoSavedAt(new Date().toISOString());
        queryClient.invalidateQueries({ queryKey: ["author-drafts"] });
      } catch { /* silent — retries next minute */ }
    }, 60000);
    return () => clearInterval(t);
  }, [user, data?.author_status, queryClient]);

  if (loading) return <div className="py-24 text-center text-gray-500">Loading…</div>;
  if (!user) {
    navigate("/login");
    return null;
  }

  const approved = data?.author_status === "approved";
  const submissions = data?.submissions || [];
  const drafts = draftsData?.drafts || [];

  const saveDraft = async () => {
    if (!form.title.trim()) { toast.error("Give your draft a title first"); return; }
    setSavingDraft(true);
    try {
      if (draftId) {
        await api.put(`/author/drafts/${draftId}`, form);
      } else {
        const { data: d } = await api.post("/author/drafts", form);
        setDraftId(d.draft.id);
      }
      toast.success("Draft saved — it's private until you submit it");
      lastSavedRef.current = JSON.stringify(form);
      setAutoSavedAt(new Date().toISOString());
      queryClient.invalidateQueries({ queryKey: ["author-drafts"] });
    } catch (e) { toast.error(apiError(e)); }
    setSavingDraft(false);
  };

  const loadDraft = (d: any) => {
    const loaded = { title: d.title || "", summary: d.summary || "", body: d.body || "",
      hero_image_url: d.hero_image_url || "", category: d.category || "crypto" };
    setForm(loaded);
    setDraftId(d.id);
    setEditingId(null);
    lastSavedRef.current = JSON.stringify(loaded);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteDraft = async (id: string) => {
    try {
      await api.delete(`/author/drafts/${id}`);
      if (draftId === id) { setDraftId(null); setForm(EMPTY); }
      queryClient.invalidateQueries({ queryKey: ["author-drafts"] });
      toast("Draft deleted");
    } catch (e) { toast.error(apiError(e)); }
  };

  const closeDraft = () => {
    setDraftId(null);
    setForm(EMPTY);
    lastSavedRef.current = "";
    setAutoSavedAt(null);
  };

  const submit = async () => {
    setSubmitting(true);
    const payload = {
      title: form.title, summary: form.summary, body: form.body,
      hero_image_url: form.hero_image_url || null, categories: [form.category],
    };
    try {
      if (editingId) {
        await api.put(`/author/articles/${editingId}`, payload);
        toast.success("Revised story resubmitted for review");
        setEditingId(null);
      } else {
        await api.post("/author/articles", payload);
        toast.success("Story submitted for editorial review");
        if (draftId) {
          try { await api.delete(`/author/drafts/${draftId}`); } catch { /* noop */ }
          setDraftId(null);
          queryClient.invalidateQueries({ queryKey: ["author-drafts"] });
        }
      }
      setForm(EMPTY);
      lastSavedRef.current = "";
      setAutoSavedAt(null);
      queryClient.invalidateQueries({ queryKey: ["author-program-me"] });
    } catch (e) { toast.error(apiError(e)); }
    setSubmitting(false);
  };

  const startRevise = async (s: any) => {
    try {
      const { data: d } = await api.get(`/author/articles/${s.id}`);
      const art = d.article;
      setForm({ title: art.title || "", summary: art.summary || "", body: art.body || "",
        hero_image_url: art.hero_image_url || "", category: art.categories?.[0] || "crypto" });
      setEditingId(art.id);
      setEditingTitle(art.title || "");
      setDraftId(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(apiError(e)); }
  };

  const cancelRevise = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  return (
    <div data-testid="author-studio-page" className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      {approved && data?.application && !data.application.studio_onboarded && !wizardDone && (
        <AuthorOnboarding app={data.application}
          onDone={(startTour) => { setWizardDone(true); if (startTour) setTour(true); }} />
      )}
      {tour && <StudioTour onClose={() => setTour(false)} />}
      <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-[#7C3AED] mb-3">
        <PenLine className="h-4 w-4" /> Author Studio
      </div>
      <h1 className="font-heading font-black text-white text-3xl sm:text-4xl">Write a story</h1>

      {approved && data?.application && (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-green-500/25 bg-green-500/[0.07] px-4 py-2.5"
          data-testid="studio-approved-banner">
          <BadgeCheck className="h-4 w-4 text-green-400 shrink-0" />
          <p className="text-sm text-green-300 font-medium">
            Approved Author
            {data.application.handle && <span className="font-mono text-green-400/80"> · @{data.application.handle}</span>}
          </p>
          <button type="button" onClick={() => setTour(true)} data-testid="studio-take-tour"
            className="ml-auto inline-flex items-center gap-1.5 font-mono text-[11px] text-[#A78BFA] hover:text-white transition-colors">
            <Compass className="h-3.5 w-3.5" /> Take the tour
          </button>
          {data.application.handle && (
            <Link to={`/author/${data.application.handle}`} data-testid="studio-public-page-link"
              className="font-mono text-[11px] text-[#00E5FF] hover:text-white transition-colors">
              View your public page →
            </Link>
          )}
        </div>
      )}

      {!approved ? (
        <div className="mt-8 bg-[#121212] border border-white/10 rounded-xl p-8" data-testid="studio-locked">
          {data?.author_status === "suspended" ? (
            <p className="text-amber-400" data-testid="studio-suspended">
              Your author access is currently suspended. If you believe this is a mistake, contact the NewsLayer team.
            </p>
          ) : data?.author_status === "rejected" ? (
            <div data-testid="studio-rejected">
              <p className="text-gray-300">Your application wasn't approved this time.</p>
              {data?.application?.note && (
                <p className="mt-2 text-sm text-red-300/90 border border-red-500/20 bg-red-500/[0.06] rounded-lg px-3 py-2">
                  Editor note: {data.application.note}
                </p>
              )}
              <p className="text-gray-400 text-sm mt-3">
                You can update your application and{" "}
                <Link to="/author-program" className="text-[#7C3AED] underline font-medium">re-apply →</Link>
              </p>
            </div>
          ) : (
            <p className="text-gray-300">
              The studio unlocks once you're an Approved Author.{" "}
              <Link to="/author-program" className="text-[#7C3AED] underline font-medium">
                {data?.author_status === "pending" || data?.application?.status === "pending_review"
                  ? "Your application is under review." : "Join the $99/yr program →"}
              </Link>
            </p>
          )}
        </div>
      ) : (
        <>
        <AuthorEarnings />
        <StoryInsights />
        {data?.application && <ProfileCard app={data.application} />}
        <div className="mt-8 space-y-4 bg-[#121212] border border-white/10 rounded-xl p-6 sm:p-8">
          {editingId && (
            <div className="flex items-center justify-between gap-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.08] px-4 py-2.5"
              data-testid="studio-revising-banner">
              <p className="text-sm text-amber-300 font-medium line-clamp-1">
                Revising: {editingTitle}
              </p>
              <button type="button" onClick={cancelRevise} data-testid="studio-cancel-revise"
                className="font-mono text-[10px] uppercase text-gray-400 hover:text-white transition-colors shrink-0">
                Cancel
              </button>
            </div>
          )}
          {!editingId && draftId && (
            <div className="flex items-center justify-between gap-2 rounded-lg border border-[#7C3AED]/35 bg-[#7C3AED]/[0.08] px-4 py-2.5"
              data-testid="studio-draft-banner">
              <p className="text-sm text-[#c4a6ff] font-medium line-clamp-1">
                Editing draft — "Save draft" updates it, "Submit for review" sends it to the editors
              </p>
              <button type="button" onClick={closeDraft} data-testid="studio-draft-close"
                className="font-mono text-[10px] uppercase text-gray-400 hover:text-white transition-colors shrink-0">
                Close
              </button>
            </div>
          )}
          <div>
            <Label>Headline</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="A sharp, specific headline (10+ chars)"
              data-testid="studio-form-title" className="mt-1 bg-[#1A1A1A] border-white/10 text-white" />
          </div>
          <div>
            <Label>Summary / dek</Label>
            <Textarea value={form.summary} rows={2}
              onChange={(e) => setForm({ ...form, summary: e.target.value })}
              placeholder="One or two sentences that sell the story (20+ chars)"
              data-testid="studio-form-summary" className="mt-1 bg-[#1A1A1A] border-white/10 text-white" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1 bg-[#1A1A1A] border-white/10 text-white" data-testid="studio-form-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white">
                  {CATEGORY_ROUTES.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Hero image URL (optional)</Label>
              <Input value={form.hero_image_url} onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })}
                placeholder="https://…" data-testid="studio-form-image"
                className="mt-1 bg-[#1A1A1A] border-white/10 text-white" />
            </div>
          </div>
          <div>
            <Label>Story body</Label>
            <Textarea value={form.body} rows={12}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Write your full story here. Blank lines create new paragraphs. (100+ chars)"
              data-testid="studio-form-body" className="mt-1 bg-[#1A1A1A] border-white/10 text-white font-body" />
            <p className="font-mono text-[10px] text-gray-500 mt-1.5">
              {form.body.trim() ? `${form.body.trim().split(/\s+/).length} words · ~${Math.max(1, Math.round(form.body.trim().split(/\s+/).length / 220))} min read` : "0 words"}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={submit} data-testid="studio-submit-button"
              disabled={submitting || form.title.length < 10 || form.summary.length < 20 || form.body.length < 100}
              className="bg-[#7C3AED] text-white font-bold hover:bg-[#6a2dd0]">
              <Send className="h-4 w-4 mr-1.5" />
              {submitting ? "Submitting…" : editingId ? "Resubmit for review" : "Submit for review"}
            </Button>
            {!editingId && (
              <Button onClick={saveDraft} data-testid="studio-save-draft" variant="outline"
                disabled={savingDraft || !form.title.trim()}
                className="border-white/15 bg-transparent text-gray-300 hover:text-white hover:bg-white/[0.06] font-semibold">
                <FileText className="h-4 w-4 mr-1.5" />
                {savingDraft ? "Saving…" : draftId ? "Save draft" : "Save as draft"}
              </Button>
            )}
            {!editingId && autoSavedAt && (
              <span data-testid="studio-autosave-indicator"
                className="font-mono text-[10px] text-gray-500">
                Autosaved {timeAgo(autoSavedAt)}
              </span>
            )}
          </div>
        </div>
        </>
      )}

      {approved && drafts.length > 0 && (
        <div className="mt-10" data-testid="studio-drafts-list">
          <h2 className="font-heading font-bold text-white text-xl mb-1">Drafts</h2>
          <p className="font-mono text-[10px] uppercase tracking-widest text-gray-600 mb-4">
            Private — only you can see these
          </p>
          <div className="space-y-2">
            {drafts.map((d: any) => (
              <div key={d.id} data-testid={`draft-row-${d.id}`}
                className={`flex items-center justify-between gap-3 bg-[#121212] border rounded-lg px-4 py-3 ${
                  draftId === d.id ? "border-[#7C3AED]/50" : "border-white/10"}`}>
                <div className="min-w-0">
                  <p className="text-white font-medium line-clamp-1 flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5 text-[#A78BFA] shrink-0" /> {d.title}
                  </p>
                  <p className="font-mono text-[10px] text-gray-500 mt-0.5">Saved {timeAgo(d.updated_at)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button type="button" onClick={() => loadDraft(d)} data-testid={`draft-edit-${d.id}`}
                    className="inline-flex items-center gap-1 rounded-md border border-[#7C3AED]/50 bg-[#7C3AED]/10 px-2.5 py-1 font-mono text-[10px] uppercase text-[#c4a6ff] hover:bg-[#7C3AED]/25 transition-colors">
                    <PenLine className="h-3 w-3" /> Edit
                  </button>
                  <button type="button" onClick={() => deleteDraft(d.id)} data-testid={`draft-delete-${d.id}`}
                    aria-label="Delete draft"
                    className="rounded-md border border-white/10 p-1.5 text-gray-500 hover:text-red-400 hover:border-red-500/40 transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {submissions.length > 0 && (
        <div className="mt-10" data-testid="studio-submissions-list">
          <h2 className="font-heading font-bold text-white text-xl mb-4">My submissions</h2>
          <div className="space-y-2">
            {submissions.map((s: any) => (
              <div key={s.id} data-testid={`submission-row-${s.slug}`}
                className="bg-[#121212] border border-white/10 rounded-lg px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    {s.status === "published" ? (
                      <Link to={`/article/${s.slug}`} className="text-white font-medium hover:text-[#00E5FF] transition-colors line-clamp-1">{s.title}</Link>
                    ) : (
                      <p className="text-white font-medium line-clamp-1">{s.title}</p>
                    )}
                    <p className="font-mono text-[10px] text-gray-500 mt-0.5">Submitted {timeAgo(s.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {(s.status === "needs_changes" || s.status === "rejected") && approved && (
                      <button type="button" onClick={() => startRevise(s)} data-testid={`submission-revise-${s.slug}`}
                        className="inline-flex items-center gap-1 rounded-md border border-[#7C3AED]/50 bg-[#7C3AED]/10 px-2.5 py-1 font-mono text-[10px] uppercase text-[#c4a6ff] hover:bg-[#7C3AED]/25 transition-colors">
                        <PenLine className="h-3 w-3" /> Revise
                      </button>
                    )}
                    <Badge variant="outline" className={`${STATUS_STYLE[s.status] || "border-white/20 text-gray-300"} shrink-0 font-mono text-[10px] uppercase`}>
                      {s.status === "imported_pending_review" ? "in review"
                        : s.status === "needs_changes" ? "needs changes" : s.status}
                    </Badge>
                  </div>
                </div>
                {s.editor_note && (s.status === "needs_changes" || s.status === "rejected") && (
                  <p data-testid={`submission-note-${s.slug}`}
                    className="mt-2 text-[12.5px] leading-snug text-amber-300/90 border border-amber-500/20 bg-amber-500/[0.06] rounded-md px-3 py-2">
                    Editor: {s.editor_note}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
